import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Timer, Brain, Clock, Zap, Users, ArrowRight, ArrowLeft, Target, Sparkles, Settings, RefreshCw } from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const tocItems = [
  { id: "attention-spans-vary", title: "Attention Spans Vary" },
  { id: "why-25-matters", title: "Why 25 Minutes Still Matters" },
  { id: "sessions-too-long", title: "Signs Sessions Are Too Long" },
  { id: "sessions-too-short", title: "Signs Sessions Are Too Short" },
  { id: "common-variations", title: "Common Pomodoro Variations" },
  { id: "match-task-type", title: "Match Length to Task Type" },
  { id: "adjust-breaks", title: "Adjust Breaks Too" },
  { id: "csw-customize", title: "Where CSW Fits In" },
  { id: "faq", title: "FAQ" },
];

const CustomizePomodoro = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do I have to use 25-minute Pomodoro sessions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. The 25-minute default is a starting point, not a rule. Research shows that attention capacity varies based on task difficulty, emotional state, sleep quality, and individual differences. The key is finding a duration that works for your brain and task type."
        }
      },
      {
        "@type": "Question",
        "name": "How do I know if my Pomodoro sessions are too long?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Signs your sessions are too long include: rereading the same paragraph repeatedly, feeling restless or irritated, constantly checking the timer, and distractions creeping in halfway through. Shortening focus blocks can prevent attention drops."
        }
      },
      {
        "@type": "Question",
        "name": "How do I know if my Pomodoro sessions are too short?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sessions might be too short if you only get focused near the end, stopping feels disruptive, or you need more ramp-up time. This often happens with writing, problem-solving, or complex reading where deeper engagement requires longer periods."
        }
      },
      {
        "@type": "Question",
        "name": "What are the most common Pomodoro variations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Common variations include: 20/5 for low energy or ADHD-friendly studying, 30/5 for slightly longer focus, 45/10 for deep reading or writing, and 50/10 for classic deep work. Research shows that structure and breaks matter more than exact timing."
        }
      },
      {
        "@type": "Question",
        "name": "Should I use different Pomodoro lengths for different tasks?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. A simple rule: memorization works better with shorter blocks, reading with medium blocks, and writing or problem-solving with longer blocks. This aligns with cognitive load theory, which shows task complexity affects optimal focus duration."
        }
      },
      {
        "@type": "Question",
        "name": "How should I customize my Pomodoro breaks?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Breaks matter just as much as focus time. Research shows active, intentional breaks restore attention better than passive scrolling. Try standing up, stretching, drinking water, or looking away from screens. Better breaks lead to better next sessions."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How to Customize Pomodoro for Your Attention Span",
    "description": "Learn how to adjust Pomodoro session lengths to match your attention span, task type, and energy level. 25 minutes isn't perfect for everyone.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/customize/"
    },
    "keywords": ["pomodoro technique", "customize pomodoro", "attention span", "focus duration", "pomodoro variations", "study timer", "productivity", "study stream", "online workspace", "virtual study room", "focus timer", "personalized studying", "50/10 pomodoro", "45/15 pomodoro", "study with me", "online study platform"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "How to Customize Pomodoro for Your Attention Span | CSW",
    "description": "Learn how to adjust Pomodoro session lengths to match your attention span, task type, and energy level.",
    "url": "https://csw.live/pomodoro-technique/customize/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Pomodoro Customization"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Attention Span" },
      { "@type": "Thing", "name": "Focus Duration" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Pomodoro Variations" },
      { "@type": "Thing", "name": "50/10 Method" },
      { "@type": "Thing", "name": "45/15 Method" },
      { "@type": "Thing", "name": "Cognitive Load Theory" },
      { "@type": "Thing", "name": "Personalized Studying" }
    ],
    "keywords": "pomodoro technique, customize pomodoro, attention span, focus duration, pomodoro variations, study stream, online workspace, virtual study room, focus timer, personalized studying, 50/10, 45/15"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Customize Pomodoro",
        "item": "https://csw.live/pomodoro-technique/customize/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "Pomodoro 101: What It Really Is",
      path: "/pomodoro-technique/pomodoro-101/",
      description: "Clear up common misconceptions about the technique"
    },
    {
      title: "The Science of Focus",
      path: "/pomodoro-technique/science-of-focus/",
      description: "Why 25 minutes is enough (most of the time)"
    },
    {
      title: "Best Pomodoro Tools",
      path: "/pomodoro-technique/best-tools/",
      description: "Finding the right timer for you"
    },
    {
      title: "Pomodoro for ADHD",
      path: "/pomodoro-technique/adhd/",
      description: "Why structure helps distracted students"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="How to Customize Pomodoro for Your Attention Span | CSW"
        description="Learn how to adjust Pomodoro session lengths to match your attention span, task type, and energy level. 25 minutes isn't perfect for everyone."
        canonicalUrl="/pomodoro-technique/customize/"
      />
      <Header />
      <BlogReadingProgress />
      
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">Customize Pomodoro</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <Settings className="w-4 h-4" />
                <span>Customization</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                How to Customize Pomodoro for{" "}
                <span className="text-primary">Your Attention Span</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                Because 25 minutes isn't perfect for everyone.
              </p>
            </div>
          </div>
        </section>

        <article className="pb-24 relative">
          <div className="container mx-auto px-4">
            <div className="flex gap-12 max-w-6xl mx-auto">
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <BlogTableOfContents items={tocItems} />
                </div>
              </aside>

              <div className="flex-1 max-w-3xl">
                <div className="lg:hidden mb-12">
                  <BlogTableOfContents items={tocItems} />
                </div>

                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                  One of the biggest myths about the Pomodoro Technique is that <strong>everyone has to use 25 minutes</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  That's not true — and insisting on it is one of the fastest ways students give up on Pomodoro altogether.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro isn't about a specific number. It's about <strong>structure, rhythm, and recovery</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Once you understand that, customization becomes a strength — not a mistake.
                </p>

                {/* Attention Spans Vary */}
                <div id="attention-spans-vary" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Attention Spans Aren't Identical (And That's Normal)
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Some students can lock in for an hour. Others struggle to hold focus for 15 minutes.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research in cognitive psychology shows that <strong>attention capacity varies widely</strong> based on:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>task difficulty</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>emotional state</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>sleep quality</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>individual differences</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Trying to force yourself into a focus window that doesn't fit your brain often leads to frustration — not productivity.
                  </p>
                </div>

                {/* Why 25 Minutes Still Matters */}
                <div id="why-25-matters" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why the Original 25 Minutes Still Matters
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  25 minutes became the default because it balances two things:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>long enough to make progress</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>short enough to reduce resistance</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Behavioral research shows that <strong>short, time-bounded commitments increase task initiation</strong>, especially for people prone to <Link to="/pomodoro-technique/pomodoro-vs-procrastination/" className="text-primary hover:underline">procrastination</Link>. Students with <Link to="/pomodoro-technique/adhd/" className="text-primary hover:underline">ADHD</Link> often find shorter sessions (like 20/5) more sustainable.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Even if you don't stick with 25 minutes forever, it's a great starting point.
                  </p>
                </div>

                {/* Signs Sessions Are Too Long */}
                <div id="sessions-too-long" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Signs Your Pomodoro Sessions Are Too Long
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Your focus window might be <em>too long</em> if:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>you reread the same paragraph repeatedly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>you feel restless or irritated</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>you keep checking the timer</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>distractions creep in halfway through</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on vigilance decrement shows that <strong>attention drops predictably over time</strong>, especially during mentally demanding tasks (
                  <a href="https://journals.sagepub.com/doi/10.1177/0018720808318025" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Helton & Warm, 2008</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Shortening focus blocks can prevent that drop.
                  </p>
                </div>

                {/* Signs Sessions Are Too Short */}
                <div id="sessions-too-short" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Signs Your Pomodoro Sessions Are Too Short
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  On the flip side, sessions might be <em>too short</em> if:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>you only get focused near the end</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>stopping feels disruptive</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>you need more ramp-up time</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This often happens with writing, problem-solving, or complex reading. Research on flow states suggests that deeper engagement sometimes requires longer uninterrupted periods.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    That's where longer Pomodoros make sense.
                  </p>
                </div>

                {/* Common Variations */}
                <div id="common-variations" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Settings className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Common Pomodoro Variations Students Use
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Here are variations students commonly adopt — all supported by research on structured focus and breaks:
                </p>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-8">
                  <ul className="space-y-4 text-foreground/90 font-body">
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">20/5</span>
                      <span>— for low energy or ADHD-friendly studying</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">30/5</span>
                      <span>— slightly longer focus, still light</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">45/10</span>
                      <span>— deep reading or writing</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">50/10</span>
                      <span>— classic "deep work" rhythm</span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research suggests that <strong>the presence of structure and breaks matters more than exact timing</strong>.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Consistency beats precision.
                  </p>
                </div>

                {/* Match Task Type */}
                <div id="match-task-type" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Match Pomodoro Length to Task Type
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  A simple rule students find helpful:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span><strong>Memorization</strong> → shorter blocks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span><strong>Reading</strong> → medium blocks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span><strong>Writing/problem-solving</strong> → longer blocks</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This aligns with cognitive load theory, which shows that <strong>task complexity affects optimal focus duration</strong>.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Stop forcing one format on everything.
                  </p>
                </div>

                {/* Adjust Breaks Too */}
                <div id="adjust-breaks" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Adjust Breaks Too — Not Just Focus Time
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Customization isn't just about work blocks. <strong>Breaks matter just as much.</strong>
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research shows that <strong>active, intentional breaks restore attention better</strong> than passive scrolling.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  If your breaks feel rushed or ineffective, try:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>standing up</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>stretching</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>drinking water</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>looking away from screens</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Better breaks = better next session.
                  </p>
                </div>

                {/* Where CSW Fits In */}
                <div id="csw-customize" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Where CSW Supports Flexible Pomodoro Use
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This is where <strong>csw.live</strong> fits naturally.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  CSW allows:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <Timer className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>different Pomodoro lengths</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>shared study rooms</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>experimentation without judgment</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Instead of guessing what works, studying with others helps you <em>notice</em> what works — and customization happens faster in shared environments.
                </p>

                {/* Final Takeaway */}
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 mb-12">
                  <h3 className="text-xl font-display font-bold mb-4">Final Takeaway</h3>
                  <p className="text-lg font-body text-foreground/90 mb-0">
                    You don't have to study the way someone else does. Find the rhythm that matches your brain, your energy, and your task — and stick with that.
                  </p>
                </div>

                {/* FAQ Section */}
                <div id="faq" className="bg-card border border-border rounded-2xl p-8 mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight">
                        Frequently Asked Questions
                      </h2>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {faqSchema.mainEntity.map((faq, index) => (
                      <div key={index} className="border-b border-border pb-6 last:border-0 last:pb-0">
                        <h3 className="text-lg font-display font-semibold mb-2">{faq.name}</h3>
                        <p className="text-foreground/80 font-body">{faq.acceptedAnswer.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center py-8 border-t border-border">
                  <Link 
                    to="/pomodoro-technique/" 
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Pomodoro Technique</span>
                  </Link>
                  <Link 
                    to="/study-timer/" 
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full hover:bg-primary/90 transition-colors"
                  >
                    <span>Try the Pomodoro Timer</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Related Articles */}
                <div className="mt-16">
                  <h2 className="text-2xl font-display font-bold mb-8">Related Articles</h2>
                  <div className="grid gap-4">
                    {relatedArticles.map((article, index) => (
                      <Link 
                        key={index}
                        to={article.path}
                        className="group p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">{article.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default CustomizePomodoro;
